import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { CATEGORY, ENTITY, ERRORS } from '../../../constants';
import { buildRequest, getStatisticsUrlForCategory, getStatisticsImg, getCategoryPageTitle, getCategoryPageDescription } from '../../../utils';

import useFetch from '../../Hooks/useFetch';

import Breadcrumb from '../../UI/Breadcrumb/Breadcrumb';
import ContactFooter from '../../UI/ContactFooter/ContactFooter';
import Loader from '../../UI/Loader/Loader';
import Teasers from '../../UI/Teasers/Teasers';
import PageUnavailable from '../Error/PageUnavailable/PageUnavailable';
import PageNotFound from '../Error/PageNotFound/PageNotFound';
import Aside from '../../UI/Aside/Aside';

const Category = () => {
    const { t } = useTranslation();
    const [isMounted, setIsMounted] = useState(false);
    const { category } = useParams();
    const location = useLocation();
    const categoryName = location?.state?.categoryName;
    const isAlertCategory = () => category === CATEGORY.alerts;
    const [statisticsUrl, setStatisticsUrl] = useState('');
    const [alerts, isFetchingAlerts, alertsError] = useFetch(
        buildRequest(ENTITY.alerts, [CATEGORY.alerts]));
    const [categories, isFetchingCategories, categoriesError] = useFetch(
        buildRequest(ENTITY.categories));
    const [categoryDetails, isFetchingDetails, detailsError] = useFetch(
        categoryName || buildRequest(ENTITY.categoryDetails, [category]),
        categoryName || category,
        categoryName);
    const [entries, isFetchingEntries, entriesError] = useFetch(
        !isAlertCategory() && buildRequest(ENTITY.entriesByCategoryId, [category]),
        !isAlertCategory() && category);

    useEffect(() => {
        setIsMounted(true);
        return () => setIsMounted(false);
    }, []);

    useEffect(() => {
        const handleStatistics = category => setStatisticsUrl(getStatisticsUrlForCategory(category));
        handleStatistics(category);
    }, [category]);

    if (!isMounted) {
        return null;
    }

    if (!isAlertCategory() && detailsError === ERRORS.notFound) {
        return <PageNotFound />;
    }

    if (isAlertCategory() ? alertsError : (entriesError || detailsError)) {
        return <PageUnavailable />;
    }

    const teasersBody = (teasers, isFetching, categoryId) => (
        <Teasers
            teasers={teasers}
            isFetching={isFetching}
            categoryId={categoryId}
        />
    );

    const renderContent = () => {
        const isAlert = isAlertCategory();
        const isFetching = isAlert ? isFetchingAlerts : isFetchingEntries;
        if (isFetching) {
            return <Loader large spinner />;
        }

        const teasers = isAlert ? alerts : entries;
        if (!teasers?.length > 0) {
            return <p>{t('layout.category.teasers.empty')}</p>;
        }

        const categoryId = isAlert ? CATEGORY.alerts : category;
        return teasersBody(teasers, isFetching, categoryId);
    };

    const isNotCurrent = x => x.id !== category;
    const categoriesWithoutCurrent = categories.filter(isNotCurrent);
    const isNotAlert = x => x.id !== CATEGORY.alerts;
    const categoriesWithoutCurrentAndAlerts = categoriesWithoutCurrent.filter(isNotAlert);

    const categoryDetailsName = categoryDetails?.name || categoryDetails;

    const helmet = categoryDetailsName && (
        <Helmet>
            <title>{getCategoryPageTitle(categoryDetailsName)}</title>
            <meta name="description" content={getCategoryPageDescription(categoryDetailsName)} />
        </Helmet>
    );

    return (
        <>
            {helmet}
            {getStatisticsImg(statisticsUrl)}
            <article className="help-page help-page--subpage">
                <header className="help-page__header help-page__header--no-space-bottom">
                    <div className="help-page__wrapper">
                        <h1 className="help-page__heading" data-cy="category-heading">
                            {isFetchingDetails
                                ? <Loader inline dotted />
                                : categoryDetailsName}
                        </h1>
                    </div>
                </header>
                <Breadcrumb categoryName={categoryDetailsName} />

                <div className="help-page__content help-page__content--subpage constrain">
                    <div className="help-page__main-col" data-cy="category-content">
                        {renderContent()}
                    </div>
                    <Aside
                        isFetchingAlerts={isFetchingAlerts}
                        isFetchingContent={isFetchingCategories}
                        alertsError={alertsError}
                        contentError={categoriesError}
                        alerts={alerts}
                        content={categoriesWithoutCurrentAndAlerts}
                        isAlertCategory={isAlertCategory()}
                    />
                </div>
            </article>
            <ContactFooter />
        </>
    );
};

export default Category;
